/**
 * ScheduleExportModal Component
 * 
 * Modal for configuring scheduled analytics exports.
 * Allows users to set format, recipients, frequency, and view current filter configuration.
 */

import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner, Badge } from "react-bootstrap";
import { CalendarCheck } from "react-bootstrap-icons";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import {
    ExportFormat,
    ExportFrequency,
    DayOfWeek,
    ExportSchedule,
    ExportScheduleRequest
} from "../../services/export/types";
import { AnalyticsFilters } from "../../types/analytics";
import Logger from "../../utils/Logger";

const FilterPreview = styled.div`
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 16px;
    font-size: 0.9rem;
    
    .filter-item {
        margin-bottom: 4px;
        
        &:last-child {
            margin-bottom: 0;
        }
        
        .label {
            font-weight: 600;
            margin-right: 8px;
        }
    }
`;

const RecipientInput = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

const RecipientBadge = styled(Badge)`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    font-size: 0.85rem;
    
    .remove-btn {
        cursor: pointer;
        opacity: 0.7;
        
        &:hover {
            opacity: 1;
        }
    }
`;

const TimeInputRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-end;
`;

export type ScheduleExportModalProps = {
    /** Whether the modal is visible */
    show: boolean;
    /** Callback to close the modal */
    onHide: () => void;
    /** Current filters to be included in the schedule */
    filters: AnalyticsFilters;
    /** Page key for identifying the analytics page */
    pageKey: string;
    /** Page title for display */
    pageTitle: string;
    /** Customer slug for the schedule */
    customerSlug: string;
    /** Existing schedule to edit (null for new) */
    editingSchedule?: ExportSchedule | null;
    /** Callback when schedule is saved */
    onSave: (schedule: ExportScheduleRequest & { id: string }) => Promise<void>;
    /** Callback to open schedule management */
    onManageSchedules?: () => void;
};

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" }
];

const FREQUENCY_OPTIONS: { value: ExportFrequency; label: string }[] = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
];

/**
 * Format filters for display
 */
function formatFiltersDisplay(filters: AnalyticsFilters): React.ReactNode {
    const items: { label: string; value: string }[] = [];

    if (filters.startDate && filters.endDate) {
        items.push({
            label: "Date Range",
            value: `${moment(filters.startDate).format("MMM D, YYYY")} - ${moment(filters.endDate).format("MMM D, YYYY")}`
        });
    }

    if (filters.attributionId && filters.attributionId.length > 0) {
        items.push({
            label: "Attribution",
            value: filters.attributionId.join(", ")
        });
    }

    if (filters.brandId && filters.brandId.length > 0) {
        items.push({
            label: "Brand",
            value: filters.brandId.join(", ")
        });
    }

    if (filters.deviceType && filters.deviceType.length > 0) {
        items.push({
            label: "Device Type",
            value: filters.deviceType.join(", ")
        });
    }

    if (items.length === 0) {
        return <span className="text-muted">No filters applied</span>;
    }

    return items.map((item, index) => (
        <div key={index} className="filter-item">
            <span className="label">{item.label}:</span>
            <span>{item.value}</span>
        </div>
    ));
}

/**
 * Validate email address
 */
function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Modal for scheduling analytics exports
 */
const ScheduleExportModal: React.FC<ScheduleExportModalProps> = ({
    show,
    onHide,
    filters,
    pageKey,
    pageTitle,
    customerSlug,
    editingSchedule,
    onSave,
    onManageSchedules
}) => {
    const [exportName, setExportName] = useState("");
    const [format, setFormat] = useState<ExportFormat>("csv");
    const [frequency, setFrequency] = useState<ExportFrequency>("weekly");
    const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>("monday");
    const [dayOfMonth, setDayOfMonth] = useState(1);
    const [timeHour, setTimeHour] = useState(9);
    const [timeMinute, setTimeMinute] = useState(0);
    const [recipients, setRecipients] = useState<string[]>([]);
    const [newRecipient, setNewRecipient] = useState("");
    const [recipientError, setRecipientError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset form when modal opens/closes or editing schedule changes
    useEffect(() => {
        if (show) {
            if (editingSchedule) {
                setExportName(editingSchedule.exportName);
                setFormat(editingSchedule.format);
                setFrequency(editingSchedule.frequency);
                setDayOfWeek(editingSchedule.dayOfWeek || "monday");
                setDayOfMonth(editingSchedule.dayOfMonth || 1);
                const [hour, minute] = editingSchedule.timeUtc.split(":").map(Number);
                setTimeHour(hour);
                setTimeMinute(minute);
                setRecipients(editingSchedule.recipients);
            } else {
                setExportName(`${pageTitle} Export`);
                setFormat("csv");
                setFrequency("weekly");
                setDayOfWeek("monday");
                setDayOfMonth(1);
                setTimeHour(9);
                setTimeMinute(0);
                setRecipients([]);
            }
            setNewRecipient("");
            setRecipientError("");
            setError(null);
        }
    }, [show, editingSchedule, pageTitle]);

    const handleAddRecipient = () => {
        const email = newRecipient.trim();
        
        if (!email) {
            return;
        }

        if (!isValidEmail(email)) {
            setRecipientError("Please enter a valid email address");
            return;
        }

        if (recipients.includes(email)) {
            setRecipientError("This email is already added");
            return;
        }

        setRecipients([...recipients, email]);
        setNewRecipient("");
        setRecipientError("");
    };

    const handleRemoveRecipient = (email: string) => {
        setRecipients(recipients.filter(r => r !== email));
    };

    const handleRecipientKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddRecipient();
        }
    };

    const handleSave = async () => {
        // Validation
        if (!exportName.trim()) {
            setError("Export name is required");
            return;
        }

        if (recipients.length === 0) {
            setError("At least one recipient email is required");
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const timeUtc = `${String(timeHour).padStart(2, "0")}:${String(timeMinute).padStart(2, "0")}`;
            
            const scheduleData: ExportScheduleRequest & { id: string } = {
                id: editingSchedule?.id || uuidv4(),
                exportName: exportName.trim(),
                format,
                recipients,
                frequency,
                dayOfWeek: frequency === "weekly" ? dayOfWeek : undefined,
                dayOfMonth: frequency === "monthly" ? dayOfMonth : undefined,
                timeUtc,
                filters,
                pageKey,
                status: "active"
            };

            await onSave(scheduleData);
            onHide();
        } catch (err) {
            Logger.log("error", "ScheduleExportModal", "Failed to save schedule", err);
            setError("Failed to save schedule. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <CalendarCheck className="me-2" />
                    {editingSchedule ? "Edit Scheduled Export" : "Schedule Export"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Export Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={exportName}
                            onChange={(e) => setExportName(e.target.value)}
                            placeholder="Enter a name for this export"
                        />
                        <Form.Text className="text-muted">
                            A descriptive name to identify this scheduled export.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Export Format</Form.Label>
                        <Form.Control
                            as="select"
                            value={format}
                            onChange={(e) => setFormat(e.target.value as ExportFormat)}
                        >
                            <option value="csv">CSV (Raw Data)</option>
                            <option value="pdf">PDF (Formatted Report)</option>
                        </Form.Control>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Recipients</Form.Label>
                        <RecipientInput>
                            {recipients.map((email) => (
                                <RecipientBadge key={email} bg="primary">
                                    {email}
                                    <span
                                        className="remove-btn"
                                        onClick={() => handleRemoveRecipient(email)}
                                    >
                                        ×
                                    </span>
                                </RecipientBadge>
                            ))}
                        </RecipientInput>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="email"
                                value={newRecipient}
                                onChange={(e) => {
                                    setNewRecipient(e.target.value);
                                    setRecipientError("");
                                }}
                                onKeyPress={handleRecipientKeyPress}
                                placeholder="Enter email address"
                                isInvalid={!!recipientError}
                            />
                            <Button variant="outline-secondary" onClick={handleAddRecipient}>
                                Add
                            </Button>
                        </div>
                        {recipientError && (
                            <Form.Text className="text-danger">{recipientError}</Form.Text>
                        )}
                        <Form.Text className="text-muted d-block mt-1">
                            Add email addresses that will receive the export. Supports distribution lists.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Frequency</Form.Label>
                        <Form.Control
                            as="select"
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value as ExportFrequency)}
                        >
                            {FREQUENCY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {frequency === "weekly" && (
                        <Form.Group className="mb-3">
                            <Form.Label>Day of Week</Form.Label>
                            <Form.Control
                                as="select"
                                value={dayOfWeek}
                                onChange={(e) => setDayOfWeek(e.target.value as DayOfWeek)}
                            >
                                {DAYS_OF_WEEK.map((day) => (
                                    <option key={day.value} value={day.value}>
                                        {day.label}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    )}

                    {frequency === "monthly" && (
                        <Form.Group className="mb-3">
                            <Form.Label>Day of Month</Form.Label>
                            <Form.Control
                                as="select"
                                value={dayOfMonth}
                                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                            >
                                {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                                    <option key={day} value={day}>
                                        {day}
                                    </option>
                                ))}
                            </Form.Control>
                            <Form.Text className="text-muted">
                                Days 1-28 are supported for consistent scheduling.
                            </Form.Text>
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Time (UTC)</Form.Label>
                        <TimeInputRow>
                            <Form.Control
                                as="select"
                                value={timeHour}
                                onChange={(e) => setTimeHour(Number(e.target.value))}
                                style={{ width: "100px" }}
                            >
                                {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                                    <option key={hour} value={hour}>
                                        {String(hour).padStart(2, "0")}
                                    </option>
                                ))}
                            </Form.Control>
                            <span>:</span>
                            <Form.Control
                                as="select"
                                value={timeMinute}
                                onChange={(e) => setTimeMinute(Number(e.target.value))}
                                style={{ width: "100px" }}
                            >
                                {[0, 15, 30, 45].map((minute) => (
                                    <option key={minute} value={minute}>
                                        {String(minute).padStart(2, "0")}
                                    </option>
                                ))}
                            </Form.Control>
                            <span className="text-muted">UTC</span>
                        </TimeInputRow>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Filter Configuration</Form.Label>
                        <FilterPreview>
                            {formatFiltersDisplay(filters)}
                        </FilterPreview>
                        <Form.Text className="text-muted">
                            These filters will be applied when generating the export.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {onManageSchedules && (
                    <Button variant="link" onClick={onManageSchedules} className="me-auto">
                        Manage Schedules
                    </Button>
                )}
                <Button variant="secondary" onClick={onHide} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : editingSchedule ? (
                        "Update Schedule"
                    ) : (
                        "Create Schedule"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ScheduleExportModal;
